import React from 'react';
import { theme } from '../../utils/theme';

const LumaGenerations = ({ lumaContent, lumaContentLoading, onDownloadVideo, onDeleteGeneration, onDeleteAll }) => {
    const tableStyles = {
        wrapper: `${theme.card.base} bg-opacity-5 overflow-hidden`,
        table: "min-w-full",
        header: "border-b border-[#6D9773]/20",
        headerCell: "px-6 py-3 text-left text-xs font-medium text-[#0C3B2E] uppercase tracking-wider",
        row: "border-b border-[#6D9773]/10 hover:bg-[#6D9773]/5 transition-colors",
        cell: "px-6 py-4 text-sm text-[#0C3B2E]"
    };

    const getStatusBadgeClass = (state) => {
        switch (state) {
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'failed':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'pending':
            case 'processing':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    if (lumaContentLoading) {
        return (
            <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className={tableStyles.wrapper}>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Luma Generations</h2>
                <button
                    onClick={onDeleteAll}
                    className={`${theme.button.base} ${theme.button.danger} ${theme.button.sizes.sm}`}
                >
                    Delete All Generations
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className={tableStyles.table}>
                    <thead className={tableStyles.header}>
                        <tr>
                            <th className={tableStyles.headerCell}>Generation ID</th>
                            <th className={tableStyles.headerCell}>Status</th>
                            <th className={tableStyles.headerCell}>Model</th>
                            <th className={tableStyles.headerCell}>Resolution</th>
                            <th className={tableStyles.headerCell}>Duration</th>
                            <th className={tableStyles.headerCell}>Created At</th>
                            <th className={tableStyles.headerCell}>Prompt</th>
                            <th className={tableStyles.headerCell}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lumaContent?.generations?.map(generation => (
                            <tr key={generation.id} className={tableStyles.row}>
                                <td className={tableStyles.cell}>
                                    <span className="font-mono text-xs">{generation.id}</span>
                                </td>
                                <td className={tableStyles.cell}>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(generation.state)}`}>
                                        {generation.state}
                                    </span>
                                </td>
                                <td className={tableStyles.cell}>
                                    <span className="font-medium">{generation.model || generation.request?.model}</span>
                                </td>
                                <td className={tableStyles.cell}>
                                    {generation.request?.resolution || 'Default'}
                                </td>
                                <td className={tableStyles.cell}>
                                    {generation.request?.duration || '5s'}
                                </td>
                                <td className={tableStyles.cell}>
                                    {new Date(generation.created_at).toLocaleString()}
                                </td>
                                <td className={`${tableStyles.cell} max-w-md`}>
                                    <p className="truncate" title={generation.request?.prompt}>
                                        {generation.request?.prompt}
                                    </p>
                                </td>
                                <td className={tableStyles.cell}>
                                    <div className="flex gap-2">
                                        {generation.assets?.video_url && (
                                            <button
                                                onClick={() => onDownloadVideo(generation.assets.video_url)}
                                                className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.xs} flex items-center gap-1`}
                                                title="Download video"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                <span>Download</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => onDeleteGeneration(generation.id)}
                                            className={`${theme.button.base} ${theme.button.danger} ${theme.button.sizes.xs} flex items-center gap-1`}
                                            title="Delete generation"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {lumaContent?.has_more && (
                <div className="mt-4">
                    <div className="flex justify-center items-center text-sm text-gray-600">
                        Showing {lumaContent.generations.length} of {lumaContent.count} generations
                    </div>
                </div>
            )}
        </div>
    );
};

export default LumaGenerations;